/**
 * Phase 2 keyboard-flow logic — PURE module (no React, no Lexical), mirroring
 * model.js's discipline so the transition table, auto-format rules, and
 * autocomplete matching are unit-testable without an editor.
 *
 * Two distinct advance keys (approved 2026-07-08; Tab unified 2026-07-15):
 *  - ENTER = "give me the contextually-right next block" — the Final-Draft
 *    smart flow (heading→action, character→dialogue, …). Empty element
 *    converts in place; mid-text splits same-type; end-of-text inserts the
 *    ENTER_TARGET block.
 *  - TAB = "cycle this element's TYPE through every type." Forward Tab and
 *    backward Shift+Tab walk the SAME complete 7-type cycle, so a writer can
 *    reach any element type from any element type. Empty element converts in
 *    place; a filled element spawns the next-typed block after it.
 *    (Was a 4-type cycle that stranded dialogue / parenthetical / shot —
 *    "Tab doesn't reach all elements", fixed 2026-07-15.)
 */
/** What Enter (at end of element) creates / converts to. */
export const ENTER_TARGET = Object.freeze({
  scene_heading: 'action',
  action: 'action',
  character: 'dialogue',
  dialogue: 'action',
  parenthetical: 'dialogue',
  transition: 'scene_heading',
  shot: 'action',
});

/**
 * The Tab type cycle — EVERY element type, in on-page writing order
 * (heading, action, who speaks, how, what they say, transition, then the
 * specialised shot). Forward Tab steps +1, Shift+Tab steps -1, both wrapping.
 * Must stay a permutation of model.js's ELEMENT_TYPES so Tab can always
 * reach all seven — pinned by a completeness test in transitions.test.js.
 */
export const TYPE_CYCLE = Object.freeze([
  'scene_heading',
  'action',
  'character',
  'parenthetical',
  'dialogue',
  'transition',
  'shot',
]);

/** Next (or previous) type in the complete Tab cycle. Unknown types are
 * returned unchanged — defensive only; every screenplay type is in the
 * cycle by construction. */
export function cycleType(type, backward = false) {
  const i = TYPE_CYCLE.indexOf(type);
  if (i === -1) return type;
  const len = TYPE_CYCLE.length;
  return TYPE_CYCLE[(i + (backward ? -1 : 1) + len) % len];
}

/* ── Scene-heading prefix completion ─────────────────────────────────── */

const HEADING_PREFIXES = Object.freeze({
  int: 'INT.',
  ext: 'EXT.',
  est: 'EST.',
  'i/e': 'I/E.',
});

// Derived once — the canonical alternation must never drift from
// HEADING_PREFIXES (it is also used by suggestionContext and the harvest).
const CANONICAL_ALTERNATION = Object.values(HEADING_PREFIXES)
  .map((p) => p.replace(/[./]/g, '\\$&'))
  .join('|');
export const CANONICAL_PREFIX_RE = new RegExp(`^(${CANONICAL_ALTERNATION})\\s+`);
const CANONICAL_PREFIX_RE_I = new RegExp(`^(${CANONICAL_ALTERNATION})\\s+(.*)$`, 'i');

// Prefix + the delimiter the writer just typed ("." or whitespace). A bare
// "int" never completes — it would hijack INTERCUT and similar words.
// Exported: the Fountain importer (fountain.js) reuses this SAME regex to
// classify scene headings, so the typing UI and the importer can never
// disagree about what counts as an INT./EXT./EST./I/E. prefix.
export const HEADING_PREFIX_RE = /^(int|ext|est|i\/e)(\.\s*|\s+)/i;

/**
 * If `text` starts with a completable scene-heading prefix in non-canonical
 * form, return { text, delta } with the prefix normalized to "INT. " form
 * (delta = length change, for caret adjustment). Returns null when nothing
 * should change — including when already canonical, so the node transform
 * that applies this is idempotent and cannot loop.
 *
 * This is the ONE approved place where typed case changes in the data; all
 * other uppercasing is CSS presentation.
 */
export function completeSceneHeadingPrefix(text) {
  const m = HEADING_PREFIX_RE.exec(text);
  if (!m) return null;
  const word = HEADING_PREFIXES[m[1].toLowerCase()]; // e.g. "INT."
  // Once the prefix WORD is canonical, the transform is done forever — it
  // must never police the writer's whitespace after it (a second space, or
  // a deliberately deleted one, is theirs). Fighting those keystrokes was
  // adversarial finding F1: silent text alteration beyond the approved rule.
  if (text.startsWith(word)) return null;
  const canonical = `${word} `;
  const typed = m[0];
  return {
    text: canonical + text.slice(typed.length),
    delta: canonical.length - typed.length,
    typedLength: typed.length,
  };
}

/* ── Parenthetical helpers ───────────────────────────────────────────── */

/**
 * The auto-inserted parenthetical scaffolding. Single owner — nodes.js and
 * the plugins reference these instead of re-encoding the literal.
 */
export const PAREN_SCAFFOLD = '()';

/** True when a parenthetical holds ONLY its auto-inserted scaffolding. */
export function isParenScaffold(text) {
  return text === PAREN_SCAFFOLD;
}

/**
 * Backspace inside a fresh "()" (caret between the parens) removes the whole
 * element. Any other content or caret position gets default backspace — the
 * parens are plain editable text, not an atomic token.
 */
export function isRemovableParenthetical(text, caretOffset) {
  return isParenScaffold(text) && caretOffset === 1;
}

/* ── Autocomplete matching ───────────────────────────────────────────── */

export const TIMES_OF_DAY = Object.freeze([
  'DAY',
  'NIGHT',
  'DAWN',
  'DUSK',
  'CONTINUOUS',
  'LATER',
]);

export const COMMON_TRANSITIONS = Object.freeze([
  'CUT TO:',
  'FADE OUT.',
  'FADE IN:',
  'DISSOLVE TO:',
  'SMASH CUT TO:',
  'MATCH CUT TO:',
  'FADE TO BLACK.',
]);

/**
 * Strict-prefix matches (case-insensitive), excluding an exact match — when
 * the writer has fully typed a known value there is nothing to accept, and
 * the popup must close so Enter/Tab go back to element cycling.
 */
export function matchSuggestions(query, candidates, limit = 6) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const out = [];
  for (const c of candidates) {
    const lc = c.toLowerCase();
    if (lc.startsWith(q) && lc !== q) {
      out.push(c);
      if (out.length >= limit) break;
    }
  }
  return out;
}

/**
 * Given a block's type, full text, and the caret offset within it, decide
 * what autocomplete context applies. Returns null (no popup) unless the
 * caret sits at the END of the query region — accepting a suggestion
 * mid-word would clobber text the writer already committed to.
 *
 * Context: { kind: 'character'|'location'|'time'|'transition',
 *            query, replaceFrom }
 * replaceFrom is the offset in `text` from which an accepted suggestion
 * replaces (always through to the end — suggestions only fire at the tip).
 */
export function suggestionContext(type, text, caretOffset) {
  if (caretOffset !== text.length) return null; // only suggest at the tip
  if (type === 'character') {
    if (!text.trim()) return null;
    return { kind: 'character', query: text, replaceFrom: 0 };
  }
  if (type === 'transition') {
    if (!text.trim()) return null;
    return { kind: 'transition', query: text, replaceFrom: 0 };
  }
  if (type === 'scene_heading') {
    const m = CANONICAL_PREFIX_RE.exec(text);
    if (!m) return null;
    const afterPrefix = m[0].length;
    const dash = text.lastIndexOf(' - ');
    if (dash >= afterPrefix) {
      // After " - ": time-of-day context.
      const from = dash + 3;
      return { kind: 'time', query: text.slice(from), replaceFrom: from };
    }
    const query = text.slice(afterPrefix);
    if (!query.trim()) return null;
    return { kind: 'location', query, replaceFrom: afterPrefix };
  }
  return null;
}

/**
 * Harvest in-script suggestion sources from a serialized elements array
 * (pure — the plugin passes the current blocks). Dedupes case-insensitively,
 * keeping the first-seen (as-typed) form.
 */
export function buildSuggestionIndex(elements) {
  const characters = [];
  const locations = [];
  const seenChar = new Set();
  const seenLoc = new Set();
  for (const el of elements) {
    if (el.type === 'character') {
      // "MAMA OTIS (V.O.)" indexes as "MAMA OTIS".
      const name = el.text.replace(/\s*\(.*\)\s*$/, '').trim();
      const key = name.toLowerCase();
      if (name && !seenChar.has(key)) {
        seenChar.add(key);
        characters.push(name);
      }
    } else if (el.type === 'scene_heading') {
      const m = CANONICAL_PREFIX_RE_I.exec(el.text);
      if (!m) continue;
      // Locations end at the LAST " - " (mirrors suggestionContext's time
      // boundary) so multi-dash locations like "COACH - TRAIN" survive.
      const dash = m[2].lastIndexOf(' - ');
      const loc = (dash === -1 ? m[2] : m[2].slice(0, dash)).trim();
      const key = loc.toLowerCase();
      if (loc && !seenLoc.has(key)) {
        seenLoc.add(key);
        locations.push(loc);
      }
    }
  }
  return { characters, locations };
}
